import React, { createContext, useContext, useEffect, useState } from "react";

import api from "../services/api";
import { listWorkspaces } from "../services/workspace/api";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [partner, setPartner] = useState(
    JSON.parse(localStorage.getItem("@App:partner")),
  );

  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("@App:user")),
  );

  const [permissions, setPermissions] = useState(
    JSON.parse(localStorage.getItem("@App:permissions")),
  );

  const [workspaces, setWorkspaces] = useState(
    JSON.parse(localStorage.getItem("@App:workspaces")),
  );

  const [workspaceId, setWorkspaceId] = useState(localStorage.getItem("@App:workspaceId"));

  async function Login(userData) {
    const response = await api.post("/v0/login", userData).then((res) => res.data);

    localStorage.setItem("@App:token", response.token);

    setWorkspaces(response.workspaces);
    localStorage.setItem("@App:workspaces", JSON.stringify(response.workspaces));

    CheckCurrentWorkspace(response.workspaces)

    await RefreshUser();
  }

  useEffect(() => {
    window.addEventListener('logout', Logout);

    return () => {
      window.removeEventListener('logout', Logout);
    };
  }, []);

  async function RefreshUser() {
    const permissionResponse = await GetPermissions();
    setPermissions(permissionResponse);
    localStorage.setItem(
      "@App:permissions", JSON.stringify(permissionResponse));

    const partnerResponse = await GetPartner();
    setPartner(partnerResponse);
    localStorage.setItem("@App:partner", JSON.stringify(partnerResponse));

    const userResponse = await GetUser();
    setUser(userResponse);
    localStorage.setItem("@App:user", JSON.stringify(userResponse));
  }

  async function GetPartner() {
    return await api
      .get("/v0/partner", {
        headers: {
          "x-access-token": localStorage.getItem("@App:token"),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          Logout();
        }
      });
  }

  async function GetUser() {
    return await api
      .get("/v0/user", {
        headers: {
          "x-access-token": localStorage.getItem("@App:token"),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          Logout();
        }
      });
  }

  async function GetPermissions() {
    return await api
      .get("/v0/user/permissions", {
        headers: {
          "x-access-token": localStorage.getItem("@App:token"),
        },
      })
      .then((res) => res.data.data)
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          Logout();
        }
      });
  }

  async function Register(userData) {
    let signed;
    await api
      .post("/v0/register", userData)
      .then((res) => res.data)
      .then((data) => {
        if (data.token) {
          localStorage.setItem("@App:token", data.token);
          signed = true;
        } else {
          signed = false;
        }
      });
    if (signed) {
        await ListWorkspaces();
        await RefreshUser();
    }
    return signed;
  }

  function Logout() {
    localStorage.removeItem("@App:partner");
    localStorage.removeItem("@App:user");
    localStorage.removeItem("@App:token");
    localStorage.removeItem("@App:permissions");
    localStorage.removeItem("@App:workspaces");
    setPartner(null);
    setUser(null);
    setWorkspaces(null);
  }

  async function UpdateUser(payload) {
    const response = await api.put("/v0/user", payload);

    const newUser = { ...user, ...response.data.data[0] };

    localStorage.setItem("@App:user", JSON.stringify(newUser));

    setUser(newUser);
  }

  async function UpdatePartner(payload) {
    const response = await api.put("/v0/partner", payload);

    const newPartner = { ...partner, ...response.data.data[0] };

    localStorage.setItem("@App:partner", JSON.stringify(newPartner));
    setPartner(newPartner);
  }

  async function ConfirmEmail(email, code) {
    const response = await api.get(`/v0/email-confirmation/${email}/${code}`);

    localStorage.setItem("@App:token", response.data.token);
    await ListWorkspaces();
    await RefreshUser();
  }

  async function ResetPassword(password, code) {
    const response = await api
      .post(`/v0/reset-password`, { password, code })
      .then((res) => res.data);

    localStorage.setItem("@App:token", response.token);

    await RefreshUser();
  }

  async function ResendConfirmationEmail(email) {
    return api.get(`/v0/re-send-email-confirmation/${email}`);
  }

  function HasPermission(permission) {
    return permissions?.includes(permission);
  }

  function ChangeWorkspace(workspaceId) {
    localStorage.setItem("@App:workspaceId", workspaceId);
    setWorkspaceId(workspaceId);
  }

  function UpdateWorkspaces(workspaces) {
    localStorage.setItem("@App:workspaces", JSON.stringify(workspaces));
    setWorkspaces(workspaces);
  }

  async function ListWorkspaces() {
    await listWorkspaces()
      .then((response) => {
        UpdateWorkspaces(response.workspaces);
        CheckCurrentWorkspace(response.workspaces)
      });
  }

  function CheckCurrentWorkspace(workspaces) {
    const currentWorkspace = localStorage.getItem("@App:workspaceId");

    if (currentWorkspace) {
      const workspace = workspaces.find((workspace) => workspace.external_id == currentWorkspace);

      if (!workspace) {
        localStorage.setItem("@App:workspaceId", workspaces[0].external_id);
        setWorkspaceId(workspaces[0].external_id);
      }
    } else {
      localStorage.setItem("@App:workspaceId", workspaces[0].external_id);
      setWorkspaceId(workspaces[0].external_id);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        signed: Boolean(user),
        partner,
        user,
        workspaces,
        workspaceId,
        Login,
        Logout,
        Register,
        UpdateUser,
        UpdatePartner,
        ResetPassword,
        ConfirmEmail,
        ResendConfirmationEmail,
        GetUser,
        RefreshUser,
        HasPermission,
        ChangeWorkspace,
        UpdateWorkspaces,
        ListWorkspaces
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
